import { useIDESelectors } from '@/hooks/use-ide-selector';
import { useIDEState } from '@/hooks/use-ide-state';
import React, { useEffect } from 'react';

const LOADING_SEQUENCE = [
    'Loading environment...',
    'Initializing file system...',
    'Loading projects...',
    'Environment loaded!',
];

interface OutputPanelProps {
    id?: string;
    className?: string;
}

export const OutputPanel: React.FC<OutputPanelProps> = ({ id, className = 'h-full flex flex-col' }) => {
    const { addOutputMessage, markOutputInitialized } = useIDEState();
    const outputPanel = useIDESelectors.useOutputPanel();

    useEffect(() => {
        // Only run loading sequence if not already initialized and the panel is visible
        if (outputPanel.hasInitialized) {
            return;
        }

        let index = 0;
        const interval = setInterval(() => {
            if (index < LOADING_SEQUENCE.length) {
                addOutputMessage(LOADING_SEQUENCE[index], 'info');
                index++;
            } else {
                clearInterval(interval);
                // Mark as initialized after loading sequence completes
                markOutputInitialized();
            }
        }, 800);

        return () => clearInterval(interval);
    }, [addOutputMessage, markOutputInitialized, outputPanel.hasInitialized]);

    return (
        <div id={id} className={className}>
            {/* Tab Bar */}
            <div
                className='h-6 bg-vscode-bg-tertiary border-t border-b border-gray-600/30 shadow-sm flex items-center'
                onContextMenu={e => e.preventDefault()}
            >
                <div className='flex items-center px-3 py-1 text-xs cursor-pointer bg-[#1e1e1e] text-vscode-text border-t-2 border-t-[#007acc]'>
                    <span className='mr-2'>📋</span>
                    <span>Output</span>
                </div>
            </div>

            {/* Panel Content */}
            <div className='flex-1 p-4 overflow-auto'>
                <div className='text-xs font-mono'>
                    {(outputPanel.messages || []).map(message => {
                        const getMessageColor = () => {
                            switch (message.type) {
                                case 'info':
                                    return 'text-vscode-text-muted';
                                case 'success':
                                    return 'text-green-400';
                                case 'warning':
                                    return 'text-yellow-400';
                                case 'error':
                                    return 'text-red-400';
                                case 'debug':
                                    return 'text-blue-400';
                                default:
                                    return 'text-vscode-text-muted';
                            }
                        };

                        return (
                            <div key={message.id} className={`mb-1 ${getMessageColor()}`}>
                                {message.message}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
